/**
 * LocalWeatherPage · v2.7.33 (2026-02-21) — PUBLIC route
 * ═════════════════════════════════════════════════════════════════════
 * Public-facing /local-weather page. Embeds the same RainViewer radar
 * + NWS warning polygons used inside Weather Commander, but with no
 * auth requirement and admin-controlled CMS copy + logo.
 *
 * Polling parity with internal modules: 10-second polling for active
 * alerts via the NWS public feed (no admin token required).
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Cloud, RefreshCw, AlertTriangle, ShieldAlert } from "lucide-react";
import axios from "axios";
import { nwsStroke, nwsFill, NWS_INFO } from "@/lib/nwsColors";
import { rainviewerTileUrl } from "@/lib/rainviewerTile";
import { loadGoogleMaps } from "@/lib/loadGoogleMaps";

const API = process.env.REACT_APP_BACKEND_URL;

// v2.7.38 · Migrated to shared singleton loader (was a duplicate definition).
const loadMaps = loadGoogleMaps;

export default function LocalWeatherPage() {
  const [cfg, setCfg] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [now, setNow] = useState(new Date());

  const wrapperRef = useRef(null);
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const radarOverlaysRef = useRef([]);
  const warningLayerRef = useRef([]);
  const [rvHost, setRvHost] = useState("");
  const [frames, setFrames] = useState([]);
  const [frameIdx, setFrameIdx] = useState(0);

  // CMS config — fetched once + on focus
  const reloadCfg = useCallback(() => {
    axios.get(`${API}/api/public/local-weather/config`).then((r) => setCfg(r.data)).catch(() => {});
  }, []);
  useEffect(() => { reloadCfg(); }, [reloadCfg]);

  // Active alerts — 10s polling (public NWS feed — no auth)
  const reloadAlerts = useCallback(() => {
    // Use NWS direct (public, no key) so this page works for unauthenticated visitors
    axios.get("https://api.weather.gov/alerts/active?area=OK", {
      headers: { "User-Agent": "BullerTaxi Local Weather Page", "Accept": "application/geo+json" },
    }).then((r) => {
      const features = r.data?.features || [];
      const norm = features.map((f) => ({
        id: f.id,
        event: f.properties?.event,
        severity: f.properties?.severity,
        headline: f.properties?.headline,
        areaDesc: f.properties?.areaDesc,
        expires: f.properties?.expires,
        geometry: f.geometry,
      }));
      setAlerts(norm);
      setNow(new Date());
    }).catch(() => {});
  }, []);
  useEffect(() => {
    reloadAlerts();
    const id = setInterval(reloadAlerts, 10000);
    return () => clearInterval(id);
  }, [reloadAlerts]);

  // RainViewer frames
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      fetch("https://api.rainviewer.com/public/weather-maps.json")
        .then((r) => r.json())
        .then((d) => {
          if (cancelled) return;
          setRvHost(d.host || "");
          const past = (d.radar?.past || []).slice(-12);
          const nowcast = (d.radar?.nowcast || []).slice(0, 3);
          setFrames([...past, ...nowcast]);
          setFrameIdx(Math.max(0, past.length - 1));
        }).catch(() => {});
    };
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Init Google Maps (uses the same global Maps script as the admin pages)
  useEffect(() => {
    let cancelled = false;
    loadMaps().then(() => {
      if (cancelled || !mapDivRef.current || mapRef.current) return;
      mapRef.current = new window.google.maps.Map(mapDivRef.current, {
        center: { lat: 35.5, lng: -97.5 },
        zoom: 7,
        mapTypeId: "terrain",
        streetViewControl: false,
        fullscreenControl: true,
        mapTypeControl: true,
        gestureHandling: "greedy",
      });
    }).catch(() => { /* maps key missing — show fallback */ });
    return () => { cancelled = true; };
  }, []);

  // Build radar overlays
  useEffect(() => {
    if (!mapRef.current || !rvHost || frames.length === 0) return;
    const stack = mapRef.current.overlayMapTypes;
    for (const old of radarOverlaysRef.current) {
      const i = stack.getArray().indexOf(old);
      if (i >= 0) stack.removeAt(i);
    }
    radarOverlaysRef.current = frames.map((f, i) => {
      const layer = new window.google.maps.ImageMapType({
        getTileUrl: (coord, zm) =>
          rainviewerTileUrl(rvHost, f.path, coord.x, coord.y, zm),
        tileSize: new window.google.maps.Size(256, 256),
        opacity: i === frameIdx ? 0.65 : 0,
        name: `lw-radar-${i}`,
      });
      stack.push(layer);
      return layer;
    });
  }, [rvHost, frames, frameIdx]);

  // Frame play loop
  useEffect(() => {
    if (frames.length === 0) return;
    const id = setInterval(() => setFrameIdx((i) => (i + 1) % frames.length), 500);
    return () => clearInterval(id);
  }, [frames.length]);

  useEffect(() => {
    if (!radarOverlaysRef.current.length) return;
    radarOverlaysRef.current.forEach((o, i) => {
      try { o.setOpacity(i === frameIdx ? 0.65 : 0); } catch { /* ignore */ }
    });
  }, [frameIdx]);

  // Warning polygons
  useEffect(() => {
    if (!mapRef.current) return;
    for (const p of warningLayerRef.current) p.setMap(null);
    warningLayerRef.current = [];
    for (const a of alerts) {
      const geom = a.geometry;
      if (!geom) continue;
      const stroke = nwsStroke(a.event, a.severity);
      const fill = nwsFill(a.event, a.severity);
      const drawPoly = (coords) => {
        const path = coords.map(([lng, lat]) => ({ lat, lng }));
        const poly = new window.google.maps.Polygon({
          paths: path,
          strokeColor: stroke, strokeWeight: 2.5, strokeOpacity: 0.95,
          fillColor: fill, fillOpacity: 0.22,
          map: mapRef.current,
        });
        warningLayerRef.current.push(poly);
      };
      if (geom.type === "Polygon") drawPoly(geom.coordinates[0]);
      else if (geom.type === "MultiPolygon") geom.coordinates.forEach((p) => drawPoly(p[0]));
    }
  }, [alerts]);

  // v2.7.34 · Public 8-day + 12-day extended forecast (mirrors admin tab)
  const [forecast12, setForecast12] = useState([]);
  useEffect(() => {
    axios.get(`${API}/api/public/local-weather/forecast?days=12`).then((r) => setForecast12(r.data?.rows || [])).catch(() => {});
    // Re-fetch every 6 h (backend refreshes once per 24 h, so this is safe)
    const id = setInterval(() => {
      axios.get(`${API}/api/public/local-weather/forecast?days=12`).then((r) => setForecast12(r.data?.rows || [])).catch(() => {});
    }, 6 * 60 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const description = cfg?.description || "Live Oklahoma weather radar with active NWS warnings, storm cell tracking, and 10-second polling.";
  const logoUrl = cfg?.logo_url ? `${API}${cfg.logo_url}` : null;

  return (
    <div className="min-h-screen bg-[#0c0d10] text-zinc-100" data-testid="local-weather-page">
      {/* Public header */}
      <header className="border-b border-zinc-800 bg-zinc-900/60 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {logoUrl ? (
              // CSS constraints per spec — max-w-[200px] max-h-[64px] object-contain
              // ensures even a 100MB high-res master renders as a tight badge.
              <img
                src={logoUrl}
                alt="Taxi Weather Commander"
                className="max-w-[200px] max-h-[64px] object-contain bg-white/5 rounded p-1"
                data-testid="lw-logo"
              />
            ) : (
              <div className="flex items-center gap-2">
                <Cloud className="h-6 w-6 text-sky-300"/>
                <span className="text-lg font-extrabold tracking-tight">Local Weather · Oklahoma</span>
              </div>
            )}
          </div>
          <Badge className="bg-emerald-500/20 text-emerald-200 border-emerald-400/40 text-[10px] uppercase tracking-widest tabular-nums" data-testid="lw-last-updated">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1.5 animate-pulse"/>
            Live · {now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </Badge>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* v2.7.35 · Tab strip — Description / Live Radar / Watches & Warnings / 12-Day Forecast */}
        <LocalWeatherTabs alerts={alerts} forecast12={forecast12} mapRef={mapRef} wrapperRef={wrapperRef} mapDivRef={mapDivRef} description={description} reloadAlerts={reloadAlerts}/>
      </main>

      <footer className="border-t border-zinc-800 py-4 mt-6 text-center text-[10px] text-zinc-500 uppercase tracking-widest">
        Buller Taxi · Local Weather feed · Radar by RainViewer · Alerts by U.S. NWS
      </footer>
    </div>
  );
}

// v2.7.35 · Tab strip — Description / Radar / Watches & Warnings / Forecast
function LocalWeatherTabs({ alerts, forecast12, mapRef, wrapperRef, mapDivRef, description, reloadAlerts }) {
  const [tab, setTab] = useState("radar");
  const tabs = [
    { id: "radar",      label: "Live Radar",           Icon: Cloud },
    { id: "watches",    label: "Watches & Warnings",   Icon: ShieldAlert, badge: alerts.length },
    { id: "forecast",   label: "12-Day Forecast",      Icon: Cloud },
  ];
  return (
    <div className="space-y-4" data-testid="lw-tabs">
      {/* Description always visible at top */}
      <Card className="bg-zinc-900 border-zinc-800 p-5">
        <div className="text-[10px] uppercase tracking-widest text-sky-400 font-bold mb-2">Current conditions</div>
        <div className="text-base text-zinc-200 whitespace-pre-wrap leading-relaxed" data-testid="lw-description">{description}</div>
      </Card>
      {/* Tab strip */}
      <div className="flex gap-1 border-b border-zinc-800 overflow-x-auto" data-testid="lw-tab-strip">
        {tabs.map((t) => {
          const Icon = t.Icon;
          const on = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`relative px-4 py-2.5 text-xs font-extrabold uppercase tracking-wider flex items-center gap-2 transition-colors ${on ? "text-amber-300" : "text-zinc-400 hover:text-zinc-200"}`}
              data-testid={`lw-tab-${t.id}`}
            >
              <Icon className="h-3.5 w-3.5"/>{t.label}
              {typeof t.badge === "number" && t.badge > 0 && (
                <Badge className="bg-red-500/25 text-red-100 border-red-400/60 text-[10px] font-extrabold ml-1 tabular-nums animate-pulse">{t.badge}</Badge>
              )}
              {on && <span className="absolute left-3 right-3 bottom-[-1px] h-[2px] bg-amber-400"/>}
            </button>
          );
        })}
      </div>

      {/* Tab panes */}
      {tab === "radar" && (
        <Card className="bg-zinc-900 border-zinc-800 p-3" data-testid="lw-radar-card">
          <div className="flex items-center justify-between mb-2 px-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-2">
              <Cloud className="h-3.5 w-3.5 text-sky-300"/>Live Radar · 10-second polling
            </div>
            <Badge className="bg-zinc-950 border-zinc-700 text-zinc-300 text-[10px]">{alerts.length} active NWS alerts</Badge>
          </div>
          <div ref={wrapperRef} className="relative w-full">
            <div ref={mapDivRef} className="w-full h-[520px] rounded-lg overflow-hidden border border-zinc-800" data-testid="lw-radar-map"/>
          </div>
        </Card>
      )}

      {tab === "watches" && <WatchesAndWarningsPanel alerts={alerts} reloadAlerts={reloadAlerts}/>}

      {tab === "forecast" && (
        forecast12.length > 0 ? (
          <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="lw-extended-forecast">
            <div className="text-[10px] uppercase tracking-widest text-sky-400 font-bold mb-3">12-day extended forecast · ECMWF + GFS blend</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-2">
              {forecast12.map((r, idx) => {
                const lightning = r.icon === "cloud-lightning";
                return (
                  <div key={r.date} className={`rounded-lg p-2.5 text-center border ${lightning ? "border-rose-400/40 bg-rose-950/30" : "border-zinc-800 bg-zinc-950/60"}`} data-testid={`lw-fcast-day-${idx}`}>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold truncate">
                      {idx === 0 ? "Today" : idx === 1 ? "Tomorrow" : new Date(r.date + "T12:00:00").toLocaleDateString([], { weekday: "short" })}
                    </div>
                    <div className="my-1.5 text-2xl">
                      {lightning ? "⚡" : r.icon === "cloud-rain" ? "🌧" : r.icon === "snowflake" ? "❄" : r.icon === "sun" ? "☀" : r.icon === "cloud-fog" ? "🌫" : "☁"}
                    </div>
                    <div className="text-[10px] text-zinc-500 truncate" title={r.label}>{r.label}</div>
                    <div className="mt-1.5 flex items-baseline justify-center gap-1 tabular-nums">
                      <span className="text-base font-extrabold text-zinc-100">{Math.round(r.temp_max_f)}°</span>
                      <span className="text-[10px] text-zinc-500">{Math.round(r.temp_min_f)}°</span>
                    </div>
                    {r.precip_prob_max > 0 && <div className="text-[10px] text-sky-300 mt-1">💧 {r.precip_prob_max}%</div>}
                  </div>
                );
              })}
            </div>
          </Card>
        ) : (
          <Card className="bg-zinc-900 border-zinc-800 p-5"><div className="text-sm text-zinc-500 text-center py-6">Loading forecast…</div></Card>
        )
      )}
    </div>
  );
}

// v2.7.35 · Watches & Warnings panel — 5-min auto-poll + manual refresh +
// color-coded by NWS severity. Alongside the forecast + radar tabs.
function WatchesAndWarningsPanel({ alerts, reloadAlerts }) {
  const [lastSync, setLastSync] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => { reloadAlerts(); setLastSync(new Date()); }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [reloadAlerts]);
  // Group by event type for the summary chips
  const grouped = (() => {
    const m = new Map();
    for (const a of alerts) {
      const key = a.event || "Other";
      if (!m.has(key)) m.set(key, 0);
      m.set(key, m.get(key) + 1);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  })();
  const warnings = alerts.filter((a) => /warning|emergency/i.test(a.event || ""));
  const watches = alerts.filter((a) => /watch/i.test(a.event || ""));
  const advisories = alerts.filter((a) => !/warning|emergency|watch/i.test(a.event || ""));
  return (
    <div className="space-y-4" data-testid="lw-watches-warnings">
      <Card className="bg-zinc-900 border-zinc-800 p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div className="text-[10px] uppercase tracking-widest text-rose-400 font-bold flex items-center gap-2">
            <ShieldAlert className="h-3.5 w-3.5"/>Active Watches &amp; Warnings · NWS feed
          </div>
          <div className="flex items-center gap-2 text-[10px] text-zinc-500">
            <span>Auto-refresh every 5 min · last sync {lastSync.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
            <Button size="sm" onClick={() => { reloadAlerts(); setLastSync(new Date()); }} className="bg-amber-400 text-zinc-900 hover:bg-amber-300 font-bold h-7 text-xs" data-testid="lw-watches-refresh">
              <RefreshCw className="h-3.5 w-3.5 mr-1.5"/>Refresh
            </Button>
          </div>
        </div>
        {/* Severity chips */}
        <div className="flex flex-wrap gap-2 mb-3">
          <Badge className="bg-red-500/20 text-red-200 border-red-400/60 text-[11px] font-extrabold uppercase tracking-wider">{warnings.length} Warnings</Badge>
          <Badge className="bg-yellow-400/20 text-yellow-200 border-yellow-400/60 text-[11px] font-extrabold uppercase tracking-wider">{watches.length} Watches</Badge>
          <Badge className="bg-cyan-500/20 text-cyan-200 border-cyan-400/60 text-[11px] font-extrabold uppercase tracking-wider">{advisories.length} Advisories</Badge>
        </div>
        {/* Event-type breakdown */}
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="lw-event-breakdown">
          {grouped.slice(0, 12).map(([ev, n]) => {
            const info = NWS_INFO(ev);
            return <Badge key={ev} className={`text-[10px] font-bold border ${info.tw.bg} ${info.tw.text} ${info.tw.border}`}>{ev} · {n}</Badge>;
          })}
        </div>
      </Card>
      {/* Color-coded list of all active alerts */}
      <Card className="bg-zinc-900 border-zinc-800 p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[60vh] overflow-y-auto" data-testid="lw-alerts-list">
          {alerts.length === 0 && <div className="col-span-2 text-sm text-zinc-500 py-6 text-center">No active watches or warnings. All clear in Oklahoma.</div>}
          {alerts.map((a) => {
            const info = NWS_INFO(a.event, a.severity);
            return (
              <div key={a.id} className={`rounded p-3 border-l-4 ${info.tw.bg} ${info.tw.text} border ${info.tw.border}`} style={{ borderLeftColor: info.stroke }} data-testid={`lw-alert-${a.id?.slice(-12)}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-xs font-extrabold uppercase tracking-wider">{a.event}</span>
                  <Badge className={`${info.tw.bg} ${info.tw.text} ${info.tw.border} text-[9px] font-bold uppercase`}>{info.kind}</Badge>
                </div>
                <div className="text-[11px] opacity-90 line-clamp-3 leading-relaxed">{a.headline}</div>
                <div className="text-[10px] opacity-60 mt-1.5">{(a.areaDesc || "").split(";").slice(0, 5).join("; ")}{(a.areaDesc || "").split(";").length > 5 ? "…" : ""}</div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
